import { create } from "zustand";
import type { GapAnalysisResult } from "@/components/expand/GapAnalysisPanel";
import type { ResultFingerprint } from "@/lib/resultFingerprint";

interface GapAnalysisState {
  /** Which concept the current result/pending state belongs to */
  conceptId: string | null;
  isPending: boolean;
  data: GapAnalysisResult | null;
  /** Snapshot of the corpus the audit ran against. Cleared on reset. */
  fingerprint: ResultFingerprint | null;
  error: string | null;

  /** Mark the store as having an in-flight audit driven by
   * useGapAnalysisGenerate. Stashes the fingerprint snapshot so a later
   * setResult promotes it. */
  markRunning: (conceptId: string, fingerprint: ResultFingerprint) => void;
  /** Promote the in-flight run to a completed result. ConceptId is
   * required so resume-on-mount paths (no prior markRunning call) can
   * also stamp the owning concept — without it the analyze.tsx
   * `gapStore.conceptId === conceptId` gate drops the result silently. */
  setResult: (conceptId: string, data: GapAnalysisResult) => void;
  /** Surface an error from the external runner. */
  setError: (error: string) => void;
  /** Cancel an in-flight audit (state-only — actual SSE cancel lives
   * in useGapAnalysisGenerate.cancel). */
  cancel: () => void;
  /** Clear the result (dismiss). */
  reset: () => void;
}

export const useGapAnalysisStore = create<GapAnalysisState>((set) => ({
  conceptId: null,
  isPending: false,
  data: null,
  fingerprint: null,
  error: null,

  markRunning: (conceptId, fingerprint) => {
    set({
      conceptId,
      isPending: true,
      data: null,
      fingerprint,
      error: null,
    });
  },

  setResult: (conceptId, data) => {
    set({ conceptId, data, isPending: false, error: null });
  },

  setError: (error) => {
    set({ error, isPending: false });
  },

  cancel: () => {
    set({ isPending: false });
  },

  reset: () => {
    set({
      conceptId: null,
      isPending: false,
      data: null,
      fingerprint: null,
      error: null,
    });
  },
}));
